import { consoleRoutes } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { AppLink, Field, Select } from "@sagot/ui";
import { useWorkspaceContext } from "../../../session/workspace-context";
import { useRefs } from "./builder/refs";
import { resolveChoices, usesConnector, type TemplateOptions } from "./template-options";
import { localized, type PlaceholderName, type TemplateChoices, type TemplateEntry } from "./templates";

const R = "engage.workflows.templates.requirements";
/** Where to create the missing row (actions belong to an agent: the first one is as good as any). */
const CREATE_ROUTE: Record<PlaceholderName, (slug: string, agentId: string | undefined) => string> = {
  booking_action: (slug, agentId) => (agentId ? consoleRoutes.actions(slug, agentId) : consoleRoutes.workflows(slug)),
  endpoint: (slug) => consoleRoutes.settings(slug, "webhooks"),
  team: (slug) => consoleRoutes.team(slug),
  user: (slug) => consoleRoutes.team(slug),
  lead_stage: (slug) => consoleRoutes.leads(slug),
};

interface TemplateRequirementsProps {
  entry: TemplateEntry;
  options: TemplateOptions;
  picked: TemplateChoices;
  onPick: (name: PlaceholderName, id: string) => void;
}

/** What the chosen template points at in the workspace (a booking action, an endpoint, a team ...): pick one, or create it first. */
export function TemplateRequirements({ entry, options, picked, onPick }: TemplateRequirementsProps) {
  const { t } = useTranslation();
  const { summary } = useWorkspaceContext();
  const agentId = useRefs().agents[0]?.id;
  if (entry.requires.length === 0) return null;
  const chosen = resolveChoices(entry.requires, options, picked);
  return (
    <fieldset className="space-y-3 rounded-lg border border-line bg-surface-2 p-4" data-testid="template-requirements">
      <legend className="px-1 text-xs font-bold uppercase tracking-wider text-fg-subtle">{t(`${R}.title`)}</legend>
      <p className="text-xs text-fg-muted">{t(`${R}.hint`)}</p>
      {entry.requires.map((name) => {
        const choices = options[name];
        const connector = name === "endpoint" && usesConnector(entry);
        return choices.length === 0 ? (
          <p key={name} className="text-xs font-semibold text-warning" role="alert" data-testid={`missing-${name}`}>
            {connector ? t(`${R}.connectorMissing`) : t(`${R}.missing.${name}`)}{" "}
            <AppLink className="underline" href={CREATE_ROUTE[name](summary.slug, agentId)}>{t(`${R}.create.${name}`)}</AppLink>
          </p>
        ) : (
          <Field key={name} label={connector ? t(`${R}.connectorLabel`) : t(`${R}.labels.${name}`)}>
            <Select value={chosen[name] ?? ""} onChange={(e) => { onPick(name, e.target.value); }}>
              {choices.map((choice) => <option key={choice.id} value={choice.id}>{choice.name}</option>)}
            </Select>
          </Field>
        );
      })}
    </fieldset>
  );
}

/** What the owner still has to do after creating the template (the builder's "Finish setting up" list tracks it). */
export function TemplateSetupNote({ entry }: { entry: TemplateEntry }) {
  const { t, i18n } = useTranslation();
  return (
    <section className="space-y-1 rounded-lg border border-line bg-info-soft p-4" data-testid="template-setup-note" aria-label={t(`${R}.setupTitle`)}>
      <h3 className="text-xs font-bold uppercase tracking-wider text-fg-subtle">{t(`${R}.setupTitle`)}</h3>
      <p className="text-xs text-fg-muted">{localized(entry.setup, i18n.language)}</p>
    </section>
  );
}
